import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DedupService } from '../dedup/dedup.service';
import { DedupSurface, DuplicateItem, DuplicateResolution } from '../dedup/dedup.model';
import { getFileExtension, getFileExtensionColor } from '../document-types';
import { CommonExplainModule } from 'src/app/common-explain/common-explain.module';

@Component({
  selector: 'app-duplicate-resolver',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, CommonExplainModule],
  templateUrl: './duplicate-resolver.component.html',
  styleUrls: ['./duplicate-resolver.component.scss'],
})
export class DuplicateResolverComponent {
  @Input({ required: true }) surface!: DedupSurface;
  @Input() items: DuplicateItem[] = [];

  readonly resolve = output<{ item: DuplicateItem; resolution: DuplicateResolution }>();

  expanded = false;

  constructor(
    private dedupService: DedupService,
    private translate: TranslateService
  ) {}

  get config() {
    return this.dedupService.getConfig(this.surface);
  }

  get primaryAction(): DuplicateResolution {
    return this.config.primaryAction;
  }

  get secondaryAction(): DuplicateResolution {
    return this.primaryAction === 'keep' ? 'replace' : 'keep';
  }

  get pendingCount(): number {
    return this.items.filter(i => !i.resolution).length;
  }

  labelFor(action: DuplicateResolution): string {
    return action === 'keep' ? this.config.keepLabelKey : this.config.replaceLabelKey;
  }

  resolvedLabel(item: DuplicateItem): string {
    return item.resolution === 'replace' ? this.config.resolvedReplaceKey : this.config.resolvedKeepKey;
  }

  decide(item: DuplicateItem, resolution: DuplicateResolution): void {
    this.resolve.emit({ item, resolution });
  }

  fileExt(name: string): string {
    return getFileExtension(name);
  }

  fileExtColor(name: string): string {
    return getFileExtensionColor(name);
  }

  metaLine(item: DuplicateItem): string {
    const bits: string[] = [this.translate.instant('tenders.responses.documents.dedup.already-present')];
    if (item.usedCount != null && item.usedCount > 0) {
      bits.push(this.translate.instant('tenders.responses.documents.dedup.used-count', { count: item.usedCount }));
    }
    if (item.ownerName) {
      bits.push(this.translate.instant('tenders.responses.documents.dedup.imported-by', { name: item.ownerName }));
    }
    return bits.join(' · ');
  }
}
